import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/EmptyState'
import { FileText, LinkIcon, Loader2 } from 'lucide-react'
import { invoicingApi } from '@/features/invoicing/api'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import type { InvoiceStatus, PortalSnapshot } from '@/features/invoicing/types'
import { formatDate } from '@/lib/locale'

/**
 * Public customer portal. Token in the URL grants read access to every
 * open invoice for a single customer. Per-invoice "Pay" buttons route
 * into the existing /pay/:invoiceToken flow - the portal only lists and
 * links; it never collects payment itself.
 */

const STATUS_VARIANT: Record<InvoiceStatus, 'default' | 'secondary' | 'danger' | 'outline' | 'success' | 'warning' | 'info'> = {
  draft: 'secondary', sent: 'default', viewed: 'default', partially_paid: 'default',
  paid: 'success', overdue: 'danger', void: 'secondary', canceled: 'secondary', written_off: 'secondary',
}

export function CustomerPortalPage() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<PortalSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const snap = await invoicingApi.fetchPortalSnapshot(token)
      setData(snap)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load portal')
    } finally {
      setLoading(false)
    }
  }, [token])
  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Card>
          <div className="p-8">
            <EmptyState
              icon={LinkIcon}
              title="Portal link not available"
              description={error || 'The link may have been revoked or expired.'}
            />
          </div>
        </Card>
      </div>
    )
  }

  const { customer, business, invoices } = data
  const totalOutstanding = invoices.reduce((acc, inv) => acc + Number(inv.amount_due || 0), 0)
  const currency = invoices[0]?.currency || 'USD'
  const address = [
    business.address_line1,
    business.address_line2,
    [business.city, business.state, business.postal_code].filter(Boolean).join(', '),
    business.country,
  ].filter(Boolean)

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Card>
        <div className="space-y-6 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              {business.logo_url ? (
                <img src={business.logo_url} alt={business.name || 'Logo'} className="mb-3 max-h-14" />
              ) : null}
              <div className="text-sm text-muted-foreground">Billed by</div>
              <div className="text-base font-semibold">{business.name || 'Bokapsys'}</div>
              {address.map((l, i) => (
                <div key={i} className="text-xs text-muted-foreground">{l}</div>
              ))}
              {business.email ? <div className="text-xs text-muted-foreground">{business.email}</div> : null}
              {business.phone ? <div className="text-xs text-muted-foreground">{business.phone}</div> : null}
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Account for</div>
              <div className="text-base font-semibold">{customer.display_name}</div>
              {customer.email ? <div className="text-xs text-muted-foreground">{customer.email}</div> : null}
            </div>
          </div>

          <div className="rounded-md border bg-muted/40 p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Total outstanding</div>
            <div className="mt-1 text-2xl font-bold">
              {formatInvoiceMoney(totalOutstanding, currency)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Across {invoices.length} open invoice{invoices.length === 1 ? '' : 's'}.
            </div>
          </div>

          {invoices.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Nothing outstanding"
              description="There are no open invoices on this account right now. Thank you!"
            />
          ) : (
            <div className="space-y-3">
              {invoices.map(inv => {
                const due = new Date(inv.due_date)
                const isOverdue = !Number.isNaN(due.getTime()) && due < new Date()
                return (
                  <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{inv.invoice_number}</span>
                        <Badge variant={STATUS_VARIANT[inv.status]}>{inv.status.replace(/_/g, ' ')}</Badge>
                        {isOverdue && inv.status !== 'paid' ? (
                          <Badge variant="danger">Overdue</Badge>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Issued {formatDate(inv.issue_date)} · Due {formatDate(inv.due_date)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatInvoiceMoney(inv.amount_due, inv.currency)}</div>
                      {Number(inv.amount_paid || 0) > 0 ? (
                        <div className="text-xs text-muted-foreground">
                          of {formatInvoiceMoney(inv.total, inv.currency)} (paid {formatInvoiceMoney(inv.amount_paid, inv.currency)})
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">total {formatInvoiceMoney(inv.total, inv.currency)}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {inv.pay_token ? (
                        <Button asChild>
                          <Link to={`/pay/${inv.pay_token}`}>Pay</Link>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="pt-4 text-center text-xs text-muted-foreground">
            Questions? Contact <strong>{business.name || 'Bokapsys'}</strong>
            {business.email ? <> at <a href={`mailto:${business.email}`} className="underline">{business.email}</a></> : null}
            .
          </div>
        </div>
      </Card>
    </div>
  )
}
